// SeccionPlegable.js — Envoltorio de acordeón para cualquier sección.
//
// Con `config.plegable: true` la sección se dibuja dentro de una barra que se
// abre y se cierra (el patrón "Descripción del curso" / "Speakers del curso").
// Sin esa clave nada cambia: SectionRenderer pinta la sección tal cual.
import React, { useState } from 'react';
import styled from 'styled-components';

const Marco = styled.div`
  max-width: ${p => p.$maxWidth || '1080px'};
  margin: ${p => p.$margin || '0 auto 0.9rem'};
  border: 1px solid ${p => p.$borde || '#e2e8f0'};
  border-radius: ${p => p.$radio || '8px'};
  overflow: hidden;
  background: ${p => p.$bgContenido || '#ffffff'};
`;

const Barra = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${p => p.$bg || '#f4f5f7'};
  color: ${p => p.$color || '#012965'};
  border: none;
  padding: ${p => p.$padding || '0.85rem 1.1rem'};
  font-family: inherit;
  font-size: ${p => p.$size || '0.95rem'};
  font-weight: 700;
  text-align: left;
  cursor: pointer;
`;

const Chevron = styled.span`
  margin-left: auto;
  font-size: 0.8em;
  opacity: 0.65;
  transform: rotate(${p => (p.$abierto ? '180deg' : '0deg')});
  transition: transform 0.2s;
`;

/* El contenido se desmonta al cerrar: las secciones de adentro pueden pedir
   datos al backend y no tiene sentido cargarlos si nadie los ve. */
const Cuerpo = styled.div`
  padding: ${p => p.$padding || '0.4rem 0 0.2rem'};
`;

/* Iconos por nombre. Van como SVG y no como emoji porque la conexión a la base
   guarda en utf8mb3: cualquier emoji de 4 bytes (📘, 👤) se convierte en "?"
   antes de llegar a la config. Además se ven iguales en todos los sistemas. */
const ICONOS = {
  libro: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4.5A1.5 1.5 0 0 1 3.5 3H9a3 3 0 0 1 3 3v13a2.5 2.5 0 0 0-2.5-2.5H2z" />
      <path d="M22 4.5A1.5 1.5 0 0 0 20.5 3H15a3 3 0 0 0-3 3v13a2.5 2.5 0 0 1 2.5-2.5H22z" />
    </svg>
  ),
  usuario: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </svg>
  ),
};

const Icono = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const SeccionPlegable = ({ config = {}, tl, children }) => {
  const c = config;
  const [abierto, setAbierto] = useState(c.plegado_inicial !== true);
  const titulo = (tl ? tl(c.plegable_titulo) : c.plegable_titulo) || '';

  return (
    <Marco
      $maxWidth={c.plegable_ancho}
      $margin={c.plegable_margen}
      $borde={c.plegable_borde}
      $radio={c.plegable_radio}
      $bgContenido={c.plegable_fondo_contenido}
    >
      <Barra
        type="button"
        onClick={() => setAbierto(a => !a)}
        aria-expanded={abierto}
        $bg={c.plegable_fondo}
        $color={c.plegable_color}
        $padding={c.plegable_padding}
        $size={c.plegable_tamano}
      >
        {c.plegable_icono && (
          <Icono aria-hidden="true">{ICONOS[c.plegable_icono] || c.plegable_icono}</Icono>
        )}
        <span>{titulo}</span>
        <Chevron $abierto={abierto}>▼</Chevron>
      </Barra>
      {abierto && <Cuerpo $padding={c.plegable_padding_contenido}>{children}</Cuerpo>}
    </Marco>
  );
};

export default SeccionPlegable;
